<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed, watch, onUnmounted } from 'vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import MessageSection from '../../components/Messages/MessageSection.vue'
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import Toolbar from "../../components/Toolbar.vue"
    import Tab from "../../components/Tab.vue"
    import { ICol, RenderModeType, calculateGridRowCount } from "../../lib/ChoiceTableHelpers";
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import { patient } from '../../lib/ChoiceApi';
    import { MML } from '../../lib/ChoiceApi';   
    import { MessageSectionType, DisplayStyle, LayoutOrientation } from "../../Enums/UiEnums";
    import { IPatientFaxQueueInputs, IPatientFaxQueueItem, IPatientFaxQueueResults, IPatientFaxQueueDeleteInputs, IPatientPrintQueueResults, IPatientPrintQueueItem, 
        IPatientPrintQueueInputs, IPatientPrintQueueAddClinicalSummaryInputs, IPatientPrintQueueUpdateClinicalSummaryInputs, PrintQueueType, 
        IPatientPrintQueueUpdateClinicalSummaryItem, IPatientPrintQueueGetClinicalSummaryInputs, IPatientPrintQueueDeleteInputs, IPatientFaxQueueSentInputs } from '../../lib/Models/PatientModel';
    import {ActionButtonStyle} from '../../lib/Types/Types';
    import { IToolbar, ToolbarStyle, ToolbarSize, ToolbarBackground, IToolbarDropDownMenuActionItem } from "../../lib/Toolbar"
    //import {open_fax, export_ccd, pdf_print} from "../../../types/LegacyOfficeMDFunctions"

    const emit = defineEmits(["optOutMML"]);

    enum PrintFaxViewType {
        Print = 0,
        Fax = 1
    }

    const viewType = ref<PrintFaxViewType>(PrintFaxViewType.Print);
    const gridRows = ref<number>(0);

    defineExpose({FaxSent});

    const printData = reactive<{
        startDate: string, 
        stopDate: string, 
        viewPrinted: boolean,
        searching: boolean,
        printing: boolean,
        adding: boolean,
        exporting: boolean,
        deleting: boolean,
        searchMessage: string,
        actionMessage: string,
        tableData: IPatientPrintQueueItem[]
    }>({
        startDate: "", 
        stopDate: "", 
        viewPrinted: false,
        searching: false,
        printing: false,
        adding: false,
        exporting: false,
        deleting: false,
        searchMessage: "",
        actionMessage: "",
        tableData: []
    });

    const faxData = reactive<{
        startDate: string, 
        stopDate: string, 
        viewFaxed: boolean,
        searching: boolean,
        deleting: boolean,
        faxing: boolean, 
        faxingEnabled: boolean,
        faxingActive: boolean,
        searchMessage: string,
        actionMessage: string,
        tableData: IPatientFaxQueueItem[]
    }>({
        startDate: "", 
        stopDate: "", 
        viewFaxed: false,
        searching: false,
        deleting: false,
        faxing: false,
        faxingEnabled: true,
        faxingActive: true,
        searchMessage: "",
        actionMessage: "",
        tableData: []
    });


    enum actionToolbar {
        search,
        print, 
        addClinicalSummary,
        exportClinicalSummary,
        delete,
        fax,
        mmlOptOut
    }

    const printToolbarPrintMenu = ref<IToolbarDropDownMenuActionItem[]>([
        {identifier: actionToolbar.mmlOptOut, label: "Print & Opt-Out of MyMedicalLocker", enabled: false, performAction: () => OptOutMML()}
    ])

    const printToolbarConfig = ref<IToolbar>({
        toolbarList: [
            {identifier: actionToolbar.search, label: "Search", action: ActionButtonStyle.Search, performAction: () => SearchPrintQueue()},
            {identifier: actionToolbar.print, label: "Print", action: ActionButtonStyle.Print, performAction: () => PrintItems(), menuList: printToolbarPrintMenu.value },
            {identifier: actionToolbar.addClinicalSummary, label: "Add Clinical Summary", action: ActionButtonStyle.Add, performAction: () => AddClinicalSummary()},
            {identifier: actionToolbar.exportClinicalSummary, label: "Export Clinical Summary", action: ActionButtonStyle.Export, performAction: () => ExportClinicalSummary()},
            {identifier: actionToolbar.delete, label: "Delete", action: ActionButtonStyle.Delete, performAction: () => DeletePrintItems()},
        ]
    });

    const faxToolbarConfig = ref<IToolbar>({
        toolbarList: [
            {identifier: actionToolbar.search, label: "Search", action: ActionButtonStyle.Search, performAction: () => SearchFaxQueue()},
            {identifier: actionToolbar.fax, label: "Fax", action: ActionButtonStyle.Print, performAction: () => FaxItems()},
            {identifier: actionToolbar.delete, label: "Delete", action: ActionButtonStyle.Delete, performAction: () => DeleteFaxItems()},
        ]
    });

    const faxTableColumns = ref<ICol[]>([
        { Name: '', DataField:"selected", RenderMode: RenderModeType.Selection },
        { Name: 'Type', DataField: 'fax_queue_type' },
        { Name: 'Name', DataField: 'fax_queue_display' },
        { Name: 'Size', DataField: 'fax_size' },
        { Name: 'Posted', DataField: 'posted_date' },
        { Name: 'Faxed', DataField: 'faxed_date' },
        { Name: 'Faxed By', DataField: 'faxed_by' },
    ]);

    const printTableColumns = ref<ICol[]>([
        { Name: '', DataField:"selected", RenderMode: RenderModeType.Selection },
        { Name: 'Type', DataField: 'action_type' },
        { Name: 'Description', DataField: 'action_alias' },
        { Name: 'Created', DataField: 'encounter_date' },
        { Name: 'Printed Date', DataField: 'log_date' },
        { Name: 'Printed By', DataField: 'log_name' },
    ]);

    const props = defineProps<{
        patientId: number,
        patientEid: number,
        faxActive?: number,
        faxEnabled?: number,
        faxResults?: IPatientFaxQueueResults,
        printResults?: IPatientPrintQueueResults,
        height?: number
    }>();

    onMounted(async () => {
        MessageLib.Fax.sink.faxSent(faxSentSink);
        if (props.faxActive == undefined) {
            const resp = await patient.printFaxQueueInitialize(props.patientId);
            faxData.tableData = resp?.faxList || [];
            faxData.faxingActive = resp?.faxActive == 1;
            faxData.faxingEnabled = resp?.faxEnabled == 1;
            printData.tableData = resp?.printList || [];
        }
        else {
            faxData.tableData = props.faxResults?.faxList || [];
            faxData.faxingActive = props.faxActive == 1;
            faxData.faxingEnabled = props.faxEnabled == 1;            
            printData.tableData = props.printResults?.printList || [];
        }
        windowResize();
    });

    onUnmounted(async () => {
        MessageLib.Fax.sink.faxSentDeleteSink(faxSentSink);
    });

    watch(() => props.patientId, (newPatientId,oldPatientId) => { 
        if (newPatientId != oldPatientId) {
            viewType.value = PrintFaxViewType.Print;
            printData.startDate = "";
            printData.stopDate = "";
            printData.tableData = [];
            printData.searchMessage = "";
            printData.actionMessage = "";
            printData.viewPrinted = false;
            faxData.startDate = "";
            faxData.stopDate = "";
            faxData.tableData = [];
            faxData.searchMessage = "";
            faxData.actionMessage = "";
            faxData.viewFaxed = false;
            if (newPatientId != null)
                SearchPrintQueue();
        }
    });

    const displayPrintSearchMessage = computed<boolean>(()=>  {
        if (viewType.value == PrintFaxViewType.Print && printData.searchMessage)
            return true;
        return false;
    });
    const displayFaxSearchMessage = computed<boolean>(()=>  {
        if (viewType.value == PrintFaxViewType.Fax && faxData.searchMessage)
            return true;
        return false;
    });

    async function SearchPrintQueue() {
        printData.actionMessage = "";
        printData.searchMessage = "";
        if (printData.startDate && printData.stopDate) {
            if (printData.startDate.length == 10 && printData.stopDate.length == 10) {
                if (Date.parse(printData.startDate) > Date.parse(printData.stopDate)) {
                    printData.searchMessage = "Search Error: Start Date cannot be greater than the Stop Date";
                    setTimeout(() => printData.searching = false, 250);
                    return;
                }
            }
        }
        const post_data = <IPatientPrintQueueInputs> {
            patientId: props.patientId,
            startDate: printData.startDate,
            endDate: printData.stopDate,
            viewPrinted: printData.viewPrinted
        }
        printData.searching = true;
        const resp = await patient.printQueue(post_data);
        printData.searching = false;
        printData.tableData = resp?.printList || [];
    }

    async function PrintItems() {
        printData.actionMessage = "";
        const filterList = printData.tableData.filter(o => o.selected);
        if (filterList.length <= 0) {
            printData.actionMessage = "Please select item(s) to print.";
            return;
        }
        let printList:string[] = [];
        let clinicalSummaryList: IPatientPrintQueueUpdateClinicalSummaryItem[] = [];

        filterList.forEach( (element) => {
            printList.push(`${element.action_group_item_id}|${element.encounter_id}|${element.encounter_table}`);
            if (element.action_group_item_id == 1 && element.action_alias.indexOf("Clinical Summary") == 0) {
                clinicalSummaryList.push({printQueueId: element.encounter_id});
            }
        }); 

        if (clinicalSummaryList.length > 0) {
            const post_data = <IPatientPrintQueueUpdateClinicalSummaryInputs> {
                patientId: props.patientId,
                clinicalSummaryList: clinicalSummaryList
            }
            printData.printing = true;
            const resp = await patient.updateClinicalSummary(post_data);
            printData.printing = false;

            if (!resp?.passed) {
                printData.actionMessage = resp?.message ?? "Failed to generate clinical summary for print."
                return;
            }
        }
        
        pdf_print("action_queue", printList.join("~"), props.patientEid.toString(), props.patientId.toString());
    }

    async function AddClinicalSummary() {
        printData.actionMessage = "";
        const post_data = <IPatientPrintQueueAddClinicalSummaryInputs> {
            patientId: props.patientId,
        }
        printData.adding = true;
        const resp = await patient.addClinicalSummary(post_data);
        printData.adding = false;        

        if (!resp?.passed) {
            printData.actionMessage = resp?.message ?? "Failed to add the clinical summary."
        }
    }

    async function ExportClinicalSummary() {
        printData.actionMessage = "";
        const filterList = printData.tableData.filter(o => o.selected);
        if (filterList.length <= 0) {
            printData.actionMessage = "Please select a Clinical Summary to Export.";
            return;
        }
        if (filterList.length > 1) {
            printData.actionMessage = "Only one Clinical Summary can be exported at a time.";
            return;
        }
        let clinicalSummaryFound = false;
        let printQueueId = -1;
        filterList.forEach( (element) => {
            if (element.action_group_item_id == 1 && element.action_alias.indexOf("Clinical Summary") == 0) {
                clinicalSummaryFound = true;
                printQueueId = element.encounter_id;
            }
        }); 
        if (!clinicalSummaryFound) {
            printData.actionMessage = "Only Clinical Summaries can be exported.";
            return;
        }

        const post_data = <IPatientPrintQueueGetClinicalSummaryInputs> {
            patientId: props.patientId,
            printQueueId: printQueueId
        }
        printData.exporting = true;
        const resp = await patient.getClinicalSummary(post_data);
        printData.exporting = false; 

        if (!resp?.passed) {
            printData.actionMessage = resp?.message ?? "Failed to export the Clinical Summaries.";
            return;
        }

        export_ccd(printQueueId, resp.ccdHtml ?? "", resp.ccdXml ?? "", props.patientId);
        printData.actionMessage = "A Clinical Summary was exported to your downloads location."
    }

    async function DeletePrintItems() {
        printData.actionMessage = "";
        const filterList = printData.tableData.filter(o => o.selected);
        if (filterList.length <= 0) {
            printData.actionMessage = "Please select item(s) to delete.";
            return;
        }

        let post_data = <IPatientPrintQueueDeleteInputs>{
            patientId: props.patientId,
            deleteList: Array()
        };

        let failedList:string[] = [];
        let printed = false;
        filterList.forEach( (element) => {
            if (element.print_queue_item && element.action_alias.indexOf("Clinical Summary") == -1) {
                post_data.deleteList.push({printQueueId: element.encounter_id});
            }
            else {
                failedList.push(element.action_alias);
            }
            if (element.log_date != "")
                printed = true;
        });
        if (printed) {
            printData.actionMessage = "Cannot delete selected item(s) that have been printed.";
            return;
        }

        if (failedList.length > 0) {
            printData.actionMessage = `The following items cannot be manually deleted: ${failedList.join(", ")}`;
            return;
        }

        printData.deleting = true;
        const respDelete = await patient.printQueueDelete(post_data);
        printData.deleting = false;

        if (respDelete?.passed) {
            SearchPrintQueue();
        } else {
            printData.actionMessage = respDelete?.message ?? "Failed to delete.";
        }
    }

    async function SearchFaxQueue() {
        faxData.actionMessage = "";
        faxData.searchMessage = "";
        if (faxData.startDate && faxData.stopDate) {
            if (faxData.startDate.length == 10 && faxData.stopDate.length == 10) {
                if (Date.parse(faxData.startDate) > Date.parse(faxData.stopDate)) {
                    faxData.searchMessage = "Search Error: Start Date cannot be greater than the Stop Date";
                    setTimeout(() => faxData.searching = false, 250);
                    return;
                }
            }
        }

        const post_data = <IPatientFaxQueueInputs> {
            patientId: props.patientId,
            startDate: faxData.startDate,
            endDate: faxData.stopDate,
            viewFaxed: faxData.viewFaxed
        }

        faxData.searching = true;
        const resp = await patient.faxQueue(post_data);
        faxData.searching = false;
        faxData.tableData = resp?.faxList || [];
    }

    async function FaxItems() {
        faxData.actionMessage = "";
        const filterList = faxData.tableData.filter(o => o.selected);
        if (filterList.length <= 0) {
            faxData.actionMessage = "Please select item(s) to fax.";
            return;
        }

        let identifierList:string[] = [];
        let typeList:string[] = [];
        let faxQueueList: number[] = [];
        filterList.forEach( (element) => {
            identifierList.push(element.fax_queue_identifier.toString());
            typeList.push(element.fax_queue_type);
            faxQueueList.push(element.fax_queue_id);
        });
        open_fax(identifierList.join("|") + "{FQ}" + typeList.join("|"), faxQueueList, props.patientId, props.patientEid);
    }

    async function DeleteFaxItems() {
        faxData.actionMessage = "";
        const filterList = faxData.tableData.filter(o => o.selected);
        if (filterList.length <= 0) {
            faxData.actionMessage = "Please select item(s) to delete.";
            return;
        }

        let post_data = <IPatientFaxQueueDeleteInputs>{
            patientId: props.patientId,
            faxList: Array()
        };

        let blnFaxed = false;
        filterList.forEach( (element) => {
            if (element.faxed) {
                blnFaxed = true;
            }
            post_data.faxList.push({faxQueueId: element.fax_queue_id});
        });
        if (blnFaxed) {
            faxData.actionMessage = "Cannot delete selected item(s) that have been faxed.";
            return;
        }

        faxData.deleting = true;
        const respDelete = await patient.faxQueueDelete(post_data);
        faxData.deleting = false;

        if (respDelete?.passed) {
            SearchFaxQueue();
        } else {
            faxData.actionMessage = respDelete?.message ?? "Failed to delete the fax item(s).";
        }
    }

    async function FaxSent(faxQueueList: number[]) {
        faxData.actionMessage = "";
        const post_data = <IPatientFaxQueueSentInputs>{
            patientId: props.patientId,
            faxQueueList: faxQueueList
        };

        faxData.faxing = true;
        const respSent = await patient.faxQueueSent(post_data);
        faxData.faxing = false;

        if (respSent?.passed) {
            SearchFaxQueue();
            return;
        }

        faxData.actionMessage = respSent?.message ?? "Failed to mark the selected fax item(s) as faxed.";
    }

	function faxSentSink(payload: any)
	{
        if (payload.patientId == props.patientId) {
            FaxSent(payload.faxQueueList);
        } else {
            popAlert("Failed to mark the fax as sent.  Patient identifier not found.", "Send Fax", ModalStyles.warning);
        }
    }

    function windowResize() {
        gridRows.value = calculateGridRowCount(320, props.height);
    }

    function onPrintTableSelectChanged(checked: boolean|undefined, colData: ICol, row?: Record<string, any>)
    {
        const mmlPrint = printData.tableData.some(item => item.print_queue_type_id == PrintQueueType.MMLWelcomeLetter && item.selected);
        const optOutItem = printToolbarPrintMenu.value.find(item => item.identifier == actionToolbar.mmlOptOut);
        if (optOutItem) {
            optOutItem.enabled = mmlPrint;
        }
    }

    async function OptOutMML() {
        await PrintItems();
        const resp = await MML.OptOut(props.patientId);
        if (!resp?.successful) {
            await popAlert(resp?.msg ?? "Failed to opt out of MyMedicalLocker","Opt Out",ModalStyles.danger);
        }
        emit("optOutMML");
    }

</script>

<style>
    .fixedLabelDateWidth {
        width: 60px;
    }
    .fixedDateWidth {
        width: 160px;
    }
    .fixedButtonWidth {
        width: 180px;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row">
        <div class="col">
            <Tab 
                :value="PrintFaxViewType.Print" 
                v-model="viewType" 
                :layout="LayoutOrientation.Horz"
                :width="100">                    
                Print
            </Tab>
            <Tab 
                :value="PrintFaxViewType.Fax" 
                v-model="viewType" 
                :layout="LayoutOrientation.Horz"
                :width="100">                    
                Fax
            </Tab>
        </div>
    </div>
    <div class="row mt-3" v-if="viewType == PrintFaxViewType.Print">
        <div class="fixedLabelDateWidth mt-2 label-isalus-small">
            Start
        </div>
        <div class="fixedDateWidth">
            <DatePickerNoTZ 
                v-model:value="printData.startDate" 
                :clearable="false"
                :cssFramework="'vanilla'" />
        </div>
        <div class="fixedLabelDateWidth mt-2 label-isalus-small">
            Stop
        </div>
        <div class="fixedDateWidth">
            <DatePickerNoTZ 
                v-model:value="printData.stopDate" 
                :clearable="false"
                :cssFramework="'vanilla'" />
        </div>
        <div class="col-auto mt-1">
            <input class="form-check-input ms-2 me-1 mt-1" type="checkbox" value="" v-model="printData.viewPrinted" @change="SearchPrintQueue()">
            <span label class="ps-1 pe-3 label-isalus-small">View Printed</span> 
        </div> 
    </div>
    <div class="row mt-2" v-if="displayPrintSearchMessage">
        <div class="col mt-0 label-isalus-small">
            <MessageSection :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{printData.searchMessage}}
            </MessageSection>    
        </div>
    </div>
    <div class="row" v-if="viewType == PrintFaxViewType.Print">
        <div class="col">
            <Toolbar 
              :configuration="printToolbarConfig" 
              :toolbarSize="ToolbarSize.regular" 
              :toolbarStyle="ToolbarStyle.outline"
              :toolbarBackground="ToolbarBackground.white"/> 
        </div>
    </div>
    <div class="row" v-if="viewType == PrintFaxViewType.Print">
        <div class="col">
            <ChoiceTable 
                ref="printTable"
                :columns="printTableColumns" 
                :data="printData.tableData" 
                :show-filters="false" 
                :allowSorting="true"
                fontSize="smaller"
                :itemsPerPage="gridRows"
                :showPaginationButtonsBelowChoiceTable="true"
                :key-value-data-member="'Col3Val'"
                @selectChanged="onPrintTableSelectChanged">
            </ChoiceTable>
        </div>
    </div>
    <div class="row mt-1" v-if="viewType == PrintFaxViewType.Print">
        <div class="col label-isalus-small">
            <MessageSection v-if="printData.actionMessage" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{printData.actionMessage}}
            </MessageSection>    
        </div>
    </div>

    <div class="row mt-3" v-if="viewType == PrintFaxViewType.Fax">
        <div class="fixedLabelDateWidth mt-2 label-isalus-small">
            Start
        </div>
        <div class="fixedDateWidth">
            <DatePickerNoTZ 
                v-model:value="faxData.startDate" 
                :clearable="false"
                :cssFramework="'vanilla'" />
        </div>
        <div class="fixedLabelDateWidth mt-2 label-isalus-small">
            Stop
        </div>
        <div class="fixedDateWidth">
            <DatePickerNoTZ 
                v-model:value="faxData.stopDate" 
                :clearable="false"
                :cssFramework="'vanilla'" />
        </div>
        <div class="col-auto mt-1">
            <input class="form-check-input ms-2 me-1 mt-1" type="checkbox" value="" v-model="faxData.viewFaxed" @change="SearchFaxQueue()">
            <span label class="ps-1 pe-3 label-isalus-small">View Faxed</span> 
        </div> 
    </div>
    <div class="row mt-2" v-if="displayFaxSearchMessage">
        <div class="col mt-0 label-isalus-small">
            <MessageSection :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{faxData.searchMessage}}
            </MessageSection>    
        </div>
    </div>
    <div class="row" v-if="viewType == PrintFaxViewType.Fax">
        <div class="col">
            <Toolbar 
              :configuration="faxToolbarConfig" 
              :toolbarSize="ToolbarSize.regular" 
              :toolbarStyle="ToolbarStyle.outline"
              :toolbarBackground="ToolbarBackground.white"/> 
        </div>
    </div>
    <div class="row" v-if="viewType == PrintFaxViewType.Fax">
        <div class="col">
            <ChoiceTable 
                ref="faxTable"
                :columns="faxTableColumns" 
                :data="faxData.tableData" 
                :show-filters="false" 
                :allowSorting="true"
                fontSize="smaller"
                :itemsPerPage="gridRows"
                :showPaginationButtonsBelowChoiceTable="true"
                :key-value-data-member="'Col3Val'">Search
            </ChoiceTable>
        </div>
    </div>
    <div class="row mt-2" v-if="viewType == PrintFaxViewType.Fax">
        <div class="col label-isalus-small">
            <MessageSection v-if="faxData.actionMessage" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{faxData.actionMessage}}
            </MessageSection>    
        </div>
    </div>
</template>

